/**
 * Drag / resize / rotate math for normalized subtitle layers.
 */

import { SUBTITLE_MIN_LAYER } from '@/lib/subtitles/subtitleLayer';
import { applyLayerConstraints } from '@/lib/subtitles/subtitleCoordinates';

const HANDLE_CURSORS = {
  nw: 'nwse-resize',
  n: 'ns-resize',
  ne: 'nesw-resize',
  e: 'ew-resize',
  se: 'nwse-resize',
  s: 'ns-resize',
  sw: 'nesw-resize',
  w: 'ew-resize',
  rotate: 'grab',
  move: 'move',
};

export function resizeHandleCursor(handle) {
  return HANDLE_CURSORS[handle] || 'default';
}

/** Resize layer from a handle drag (normalized deltas). */
export function resizeSubtitleLayerFromHandle(handle, startLayer, dxNorm, dyNorm, aspectRatio, options = {}) {
  const minW = Number(options.minWidth) > 0 ? Number(options.minWidth) : SUBTITLE_MIN_LAYER.width;
  const minH = Number(options.minHeight) > 0 ? Number(options.minHeight) : SUBTITLE_MIN_LAYER.height;
  let { x, y, width, height } = startLayer;
  const halfW = width / 2;
  const halfH = height / 2;
  let left = x - halfW;
  let right = x + halfW;
  let top = y - halfH;
  let bottom = y + halfH;

  const affectsLeft = handle === 'w' || handle === 'nw' || handle === 'sw';
  const affectsRight = handle === 'e' || handle === 'ne' || handle === 'se';
  const affectsTop = handle === 'n' || handle === 'nw' || handle === 'ne';
  const affectsBottom = handle === 's' || handle === 'sw' || handle === 'se';

  if (affectsLeft) left += dxNorm;
  if (affectsRight) right += dxNorm;
  if (affectsTop) top += dyNorm;
  if (affectsBottom) bottom += dyNorm;

  width = Math.max(minW, right - left);
  height = Math.max(minH, bottom - top);
  x = left + width / 2;
  y = top + height / 2;

  return applyLayerConstraints(
    { ...startLayer, x, y, width, height },
    aspectRatio,
    options,
  );
}

/** Drag layer center by normalized delta. */
export function dragSubtitleLayer(startLayer, dxNorm, dyNorm, aspectRatio, options = {}) {
  return applyLayerConstraints(
    {
      ...startLayer,
      x: startLayer.x + dxNorm,
      y: startLayer.y + dyNorm,
    },
    aspectRatio,
    options,
  );
}

/** Wrap any degree value to [-180, 180] (CSS transform–friendly). */
export function normalizeRotationSigned(deg) {
  const d = Number(deg);
  if (!Number.isFinite(d)) return 0;
  return ((((d + 180) % 360) + 360) % 360) - 180;
}

/** Pointer angle from center in degrees (0° = east, 90° = south). */
export function pointerAngleFromCenter(clientX, clientY, centerX, centerY) {
  const rad = Math.atan2(clientY - centerY, clientX - centerX);
  return (rad * 180) / Math.PI;
}

/**
 * Absolute rotation for upright text (0°) when the pointer is above the box center.
 * Prefer {@link rotationFromPointerDelta} while dragging to avoid 0°/180° flips.
 */
export function rotationFromPointer(clientX, clientY, centerX, centerY) {
  return normalizeRotationSigned(pointerAngleFromCenter(clientX, clientY, centerX, centerY) + 90);
}

/**
 * Smooth rotation: apply pointer delta since drag start (no atan2 branch jumps).
 */
export function rotationFromPointerDelta(
  clientX,
  clientY,
  centerX,
  centerY,
  startPointerAngleDeg,
  startRotationDeg,
) {
  const current = pointerAngleFromCenter(clientX, clientY, centerX, centerY) + 90;
  let delta = current - startPointerAngleDeg;
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  return normalizeRotationSigned(startRotationDeg + delta);
}

/** @deprecated Use normalizeRotationSigned */
export function normalizeRotationDegrees(deg) {
  return normalizeRotationSigned(deg);
}

export const SUBTITLE_RESIZE_HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

export function handlePositionStyle(handle, inset = -7, size = 14) {
  const half = size / 2;
  const base = { position: 'absolute', width: size, height: size, zIndex: 60 };
  const pos = { ...base };
  switch (handle) {
    case 'nw':
      return { ...pos, left: inset, top: inset, cursor: resizeHandleCursor('nw') };
    case 'n':
      return { ...pos, left: '50%', top: inset, marginLeft: -half, cursor: resizeHandleCursor('n') };
    case 'ne':
      return { ...pos, right: inset, top: inset, cursor: resizeHandleCursor('ne') };
    case 'e':
      return { ...pos, right: inset, top: '50%', marginTop: -half, cursor: resizeHandleCursor('e') };
    case 'se':
      return { ...pos, right: inset, bottom: inset, cursor: resizeHandleCursor('se') };
    case 's':
      return { ...pos, left: '50%', bottom: inset, marginLeft: -half, cursor: resizeHandleCursor('s') };
    case 'sw':
      return { ...pos, left: inset, bottom: inset, cursor: resizeHandleCursor('sw') };
    case 'w':
      return { ...pos, left: inset, top: '50%', marginTop: -half, cursor: resizeHandleCursor('w') };
    default:
      return pos;
  }
}
